<template>
	<Dialog class="mailing-list-dialogue"
			:modal="true"
			:maximizable="true"
			v-model:visible="show">

			<DataTable stripedRows
					   class="p-datatable-sm mailing-list-datatable"
					   scrollHeight="flex"
					   responsiveLayout="scroll"
					   :value="filteredSelectedContacts"
					   :rows="100"
					   :rowClass="rowClass"
					   :scrollable="true">

				<Column field="firstName" header="First name" class="flex-grow">
					<template #body="{data}">
						<InputText v-model="data.firstName" />
					</template>
				</Column>
				<Column field="lastName" header="Last name" class="flex-grow">
					<template #body="{data}">
						<InputText v-model="data.lastName" />
					</template>
				</Column>
				<Column field="jobTitle" header="Job title" class="flex-grow" v-if="windowWidth >= 1200">
					<template #body="{data}">
						<InputText v-model="data.jobTitle" />
					</template>
				</Column>
				<Column field="email" header="Email" class="flex-grow">
					<template #body="{data}">
						<InputText v-model="data.email" type="email" />
					</template>
				</Column>
				<Column field="mobile" header="Mobile" class="flex-grow">
					<template #body="{data}">
						<InputText v-model="data.mobile" />
					</template>
				</Column>
				<Column field="Ddi" header="Landline" class="flex-grow" v-if="windowWidth >= 1200">
					<template #body="{data}">
						<InputText v-model="data.ddi" />
					</template>
				</Column>
				<Column field="companyName" header="Company" class="flex-grow" v-if="!isBulkUpdateContactsEnabled && windowWidth >= 1200">
					<template #body="{data}">
						<div v-if="data.companyId && !data.isCompanyDeleted">
							<router-link :to="!data.isCompanyDeleted ? { name: 'CompanyDetails', params: { companyId: data.companyId } } : null">
								{{ data.companyName }}
							</router-link>
							<Badge class="small-grey-badge ml-1" value="inactive" v-if="!data.isCompanyActive" />
						</div>
						<span v-else>-</span>
					</template>
				</Column>
				<Column v-if="!isBulkUpdateContactsEnabled && windowWidth >= 992">
					<template #body="{data}">
						<div :title="getTooltipText(data.isCompanyActive, data.isActive)">
							<Button icon='pi pi-trash'
									@click="archiveContact(data)"
									v-if="!loading && data.isActive" />

							<Button icon='pi pi-refresh'
									@click="restoreContact(data)"
									:disabled="!data.isCompanyActive"
									v-if="!loading && !data.isActive" />
						</div>
					</template>
				</Column>
			</DataTable>

		<!--Footer-->
		<template #footer>
			<div class="flex justify-content-end mt-3">
				<Button class="sticky-button" :icon="isBulkUpdateContactsEnabled ? 'pi pi-user' : 'pi pi-users'" :label="isBulkUpdateContactsEnabled ? 'Update Individually' : 'Update All'" @click="toggleisBulkUpdateContactsEnabled" v-if="!loading" />
				<Button class="sticky-button" icon="pi pi-trash" label="Delete All" @click="archiveAllAContacts" v-if="!loading && isBulkUpdateContactsEnabled && !allContactsArchived" />
				<Button class="sticky-button" icon="pi pi-refresh" label="Restore All" @click="restoreAllContacts" v-if="!loading && isBulkUpdateContactsEnabled && !allContactsActive" />
				<Button class="sticky-button" icon="pi pi-save" :label="isBulkUpdateContactsEnabled ? 'Save all' : 'Save'" @click="saveContacts" v-if="!loading" :disabled="!hasChanges" />
			</div>
		</template>

		<ProgressSpinner v-if="loading" />
	</Dialog>
</template>

<script lang="ts">
	import Toast from '@/utils/Toast';
	import { Vue, Options  } from 'vue-class-component';
	import { ContactListMember, ContactListSearchQuery, GetContactListQuery, RestoreContactCommand, ArchiveContactCommand, 
		UpdateContactsCommand, ContactClient, RestoreContactsCommand, ArchiveContactsCommand, BulkUpdateContactsCommand} from '../api-client/api-client';
	import { CompassApi } from '../api-client/CompassApiProxy';
	import { ConfirmationOptions } from "primevue/confirmationoptions";

	@Options({
		name: 'ContactDetailsPopup',
		props: {
			selectedContact: null,
		},
		watch: {
			show(value: boolean) {
				this.$emit('updateshowContactDetailsPopup', value);
			},
		},
		  mounted() {
			window.addEventListener('resize', this.onResize);
		  },

		  beforeUnmount() {
			window.removeEventListener('resize', this.onResize);
		  },
	})

	export default class ContactDetailsPopup extends Vue {

		selectedContact!: ContactListMember;

		selectedContacts: ContactListMember[] = [];

		query: ContactListSearchQuery = new ContactListSearchQuery();

		receivedDataJSON = "";

		loading = false;

		show = true;

		isBulkUpdateContactsEnabled = false;

		windowWidth: number = window.innerWidth;

		created() {

			this.query.includeSuppliers = true;
			this.query.includeCustomers = true;
			this.query.includeOwners = true;
			this.query.ignoreDuplicateEmailAddresses = false;
			this.query.includeInactive = true;
			this.query.includeContactsWithoutEmail = false;
			this.query.includeSiteContacts = true;
			this.query.keywords = this.selectedContact.email;

			this.loadData();
		}

		loadData() {
			this.loading = true;

			const apiClient = CompassApi.contactApi(this.$store);
			const recordLimit = 10000;

			const request = new GetContactListQuery();
			request.query = this.query
			request.query.recordLimit = recordLimit;

			return apiClient
				.searchContacts(request)
				.then(response => {
					this.selectedContacts = response;
				})
				.catch(error => {
					Toast.ApiError(`Error retrieving contacts`, error);
				})
				.finally(() => {
					this.receivedDataJSON = JSON.stringify(this.selectedContacts, null, 1);
					this.loading = false;
			});
		}

		async saveContacts() {

			this.loading = true;

			const apiClient = CompassApi.contactApi(this.$store);

			if(this.isBulkUpdateContactsEnabled){
				await this.bulkUpdateContacts(apiClient)
			} else {
				await this.UpdateContacts(apiClient)
			}
		}

		async UpdateContacts(apiClient: ContactClient){
			
			const updateContactsCommand = new UpdateContactsCommand();

			updateContactsCommand.contacts = this.selectedContacts;

			apiClient.updateContacts(updateContactsCommand)
			.then(() => {
				Toast.RemoveAllGroups();
				Toast.Success('Updated contacts', 'Contact details have been updated succesfully');
				})
				.catch(error => {
					this.loading = false;
					Toast.ApiError('Error updating contact details ', error);
				})
				.finally(() => {
					window.location.reload();
				});
		}

		async bulkUpdateContacts(apiClient: ContactClient){
		
			const bulkUpdateContactsCommand = new BulkUpdateContactsCommand();
			bulkUpdateContactsCommand.contact = this.selectedContacts[0];

			apiClient.bulkUpdateContacts(bulkUpdateContactsCommand)
				.then(() => {
					Toast.RemoveAllGroups();
					Toast.Success('Updated contacts', 'Contact details have succesfully updated');
				})
				.catch(error => {
					this.loading = false;
					Toast.ApiError('Error updating contact details ', error);
				})
				.finally(() => {
					window.location.reload();
				});
		}

		async archiveContact(contact: ContactListMember) {

			const userConfirmation = await this.archiveRestoreContactConfirmation(contact, true)
			if (!userConfirmation)
				return;

			this.loading = true;

			const apiClient = CompassApi.contactApi(this.$store);
			const archiveContactCommand = new ArchiveContactCommand();
			archiveContactCommand.contact = contact;

			apiClient.archiveContact(archiveContactCommand)
				.then(() => {
					Toast.RemoveAllGroups();
					Toast.Success('Deleted contact', 'Contact has been succesfully deleted');
				})
				.catch(error => {
					this.loading = false;
					Toast.ApiError(`Error deleting contact: ${contact.displayName} `, error);
				})
				.finally(() => {
					this.loadData();
					this.loading = false;
				});
		}

		async restoreContact(contact: ContactListMember) {
			this.loading = true;

			const apiClient = CompassApi.contactApi(this.$store);
			const restoreContactCommand = new RestoreContactCommand();
			restoreContactCommand.contact = contact;

			apiClient.restoreContact(restoreContactCommand)
				.then(() => {
					Toast.RemoveAllGroups();
					Toast.Success('Restored contact', 'Contact has been succesfully restored');
				})
				.catch(error => {
					this.loading = false;
					Toast.ApiError(`Error restoring contact: ${contact.displayName} `, error);
				})
				.finally(() => {
					this.loadData();
					this.loading = false;
				});
		}

		async archiveAllAContacts() {

			const contact = this.selectedContacts[0];
			const userConfirmation = await this.archiveRestoreContactConfirmation(contact, true)
			if (!userConfirmation)
				return;

			this.loading = true;

			const apiClient = CompassApi.contactApi(this.$store);
			const restoreContactsCommand = new ArchiveContactsCommand();
			restoreContactsCommand.contact = contact;

			apiClient.archiveContacts(restoreContactsCommand)
				.then(() => {
					Toast.RemoveAllGroups();
					Toast.Success('Delete contacts', 'Contacts have been been succesfully deleted');
				})
				.catch(error => {
					this.loading = false;
					Toast.ApiError(`Error deleting contact: ${contact.displayName} `, error);
				})
				.finally(() => {
					this.loadData();
					this.loading = false;
				});
		}

		async restoreAllContacts() {

			const contact = this.selectedContacts[0];
			const userConfirmation = await this.archiveRestoreContactConfirmation(contact, false)
			if (!userConfirmation)
				return;

			this.loading = true;

			const apiClient = CompassApi.contactApi(this.$store);
			const restoreContactsCommand = new RestoreContactsCommand();
			restoreContactsCommand.contact = contact;

			apiClient.restoreContacts(restoreContactsCommand)
				.then(() => {
					Toast.RemoveAllGroups();
					Toast.Success('Restored contacts', 'Contacts have been succesfully restored');
				})
				.catch(error => {
					this.loading = false;
					Toast.ApiError(`Error restoring contacts: ${contact.displayName} `, error);
				})
				.finally(() => {
					this.loadData();
					this.loading = false;
				});
		}

		async archiveRestoreContactConfirmation(contact: ContactListMember, isArchiveCommand: boolean): Promise<boolean> {

			const plural = this.isBulkUpdateContactsEnabled ? "s" : ""
			const header = isArchiveCommand ? `Delete contact${plural}` : `Restore contact${plural}`;
			let message = `Are you sure you want to ${isArchiveCommand ? "delete" : "restore"} ${contact.displayName}?`;

			if(this.isBulkUpdateContactsEnabled)
				message = `This will ${isArchiveCommand ? "delete" : "restore"} all contacts for ${contact.displayName} are you sure?`;

			return new Promise((resolve) => {
				this.$confirm.require({
					header: header,
					message: message,
					accept: () => {
						resolve(true);
					},
					reject: () => {
						resolve(false);
					}
				} as ConfirmationOptions);
			});
		}

		isActive(data: ContactListMember): boolean {
			return data.isActive && data.isCompanyActive ? true : false;
		}

		rowClass(data: ContactListMember): string {
			return this.isActive(data) ? "" : "row-inactive";
		}

		toggleisBulkUpdateContactsEnabled(){
			this.isBulkUpdateContactsEnabled = !this.isBulkUpdateContactsEnabled;
		}

		onResize() {
			  this.windowWidth = window.innerWidth;
		}

		getTooltipText(isCompanyActive: boolean, isContactActive: boolean): string {
			return !isContactActive && !isCompanyActive ? "Company needs to be active first" : "";
		}

		get hasChanges(): boolean {
			const currentJSON = JSON.stringify(this.selectedContacts, null, 1);

			return (currentJSON != this.receivedDataJSON);
		}

		get filteredSelectedContacts(){
			return this.isBulkUpdateContactsEnabled && this.selectedContacts.length > 0 ? this.selectedContacts.slice(0, 1) : this.selectedContacts;
		}

		get allContactsArchived(){
			return this.selectedContacts.every(contact => !contact.isActive);
		}

		get allContactsActive(){
			return this.selectedContacts.every(contact => contact.isActive);
		}
	}

</script>

<style scoped lang="scss">
</style>